import React, { createContext, useContext, useState, useCallback, useLayoutEffect, useMemo } from 'react';
import {
  THEMES,
  MODES,
  DENSITIES,
  MANAGED_VARS,
  LIGHT_MODE_VARS,
  DEFAULT_THEME_ID,
  DEFAULT_DENSITY_ID,
  DEFAULT_MODE
} from '../constants/themes';

/**
 * ThemeContext — runtime appearance: colour theme × light/dark mode × density.
 *
 * Applies the selected theme's CSS-variable overrides to <html>, then (for
 * light mode) the neutral-flip overrides on top, toggles
 * data-theme / data-mode / data-density (consumed by styles/density.css and
 * components), loads any web font the theme needs, and persists choices in
 * localStorage. The default ("classic" + "dark") uses the :root values in
 * styles/variables.css.
 *
 * Implements the theming/dark-mode/density system in
 * docs/frontend/DESIGN_REDESIGN.md (§1.4, §3.6, §6.2).
 */

const THEME_KEY = 'al_theme';
const MODE_KEY = 'al_mode';
const DENSITY_KEY = 'al_density';

const ThemeContext = createContext(null);

const getTheme = (id) => THEMES.find((t) => t.id === id) || THEMES[0];

/**
 * Read a persisted id, falling back to the default if it's missing or no longer
 * valid (e.g. a theme that existed in an older build was removed). Prevents stale
 * localStorage from leaving an unrecognised id in state.
 */
function readValidId(key, validIds, fallback) {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  return stored && validIds.includes(stored) ? stored : fallback;
}

/** Add a web-font stylesheet once (idempotent). */
function ensureFont(url) {
  if (!url || typeof document === 'undefined') return;
  if (document.querySelector(`link[data-theme-font="${url}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  link.setAttribute('data-theme-font', url);
  document.head.appendChild(link);
}

function applyAppearance(themeId, mode) {
  if (typeof document === 'undefined') return;
  const theme = getTheme(themeId);
  const root = document.documentElement;
  // Reset every managed var, then layer theme, then (if light) the mode flip.
  MANAGED_VARS.forEach((key) => root.style.removeProperty(key));
  Object.entries(theme.vars).forEach(([key, value]) => root.style.setProperty(key, value));
  if (mode === 'light') {
    Object.entries(LIGHT_MODE_VARS).forEach(([key, value]) => root.style.setProperty(key, value));
  }
  root.setAttribute('data-theme', theme.id);
  root.setAttribute('data-mode', mode);
  ensureFont(theme.fontUrl);
}

function persist(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // localStorage may be unavailable (private mode) — selection still applies for the session.
  }
}

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(() =>
    readValidId(THEME_KEY, THEMES.map((t) => t.id), DEFAULT_THEME_ID)
  );
  const [mode, setModeState] = useState(() =>
    readValidId(MODE_KEY, MODES.map((m) => m.id), DEFAULT_MODE)
  );
  const [densityId, setDensityState] = useState(() =>
    readValidId(DENSITY_KEY, DENSITIES.map((d) => d.id), DEFAULT_DENSITY_ID)
  );

  // useLayoutEffect so the persisted theme/mode/density is on <html> before the
  // browser paints — avoids a flash of the default theme on load.
  useLayoutEffect(() => {
    applyAppearance(themeId, mode);
  }, [themeId, mode]);

  useLayoutEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-density', densityId);
    }
  }, [densityId]);

  const setTheme = useCallback((id) => {
    setThemeId(id);
    persist(THEME_KEY, id);
  }, []);

  const setMode = useCallback((id) => {
    setModeState(id);
    persist(MODE_KEY, id);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      persist(MODE_KEY, next);
      return next;
    });
  }, []);

  const setDensity = useCallback((id) => {
    setDensityState(id);
    persist(DENSITY_KEY, id);
  }, []);

  const value = useMemo(() => ({
    themeId,
    mode,
    densityId,
    setTheme,
    setMode,
    toggleMode,
    setDensity,
    themes: THEMES
  }), [themeId, mode, densityId, setTheme, setMode, toggleMode, setDensity]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
